import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import LeadOverview from './LeadOverview';
import { leadAPI, noteAPI, activityAPI, communicationAPI, followUpAPI, quotationAPI, leadStatusAPI, workflowAPI, settingsAPI, leadFollowUpAPI, projectAPI, paymentAPI, scopeAPI, architectAssignAPI, assignmentAPI, receiptAPI, callAPI, callingAPI } from '../api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  Building,
  Calendar as CalendarIcon,
  DollarSign,
  User,
  Clock,
  FileText,
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  Send,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  CheckCircle,
  GitBranch
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { ClickToCallButton } from '../components/ClickToCallButton';
import { formatDateTime } from '../lib/timeFormat';

export const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasPermission } = useAuth();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [calls, setCalls] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [workflowStages, setWorkflowStages] = useState([]);
  const [services, setServices] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [newQuotation, setNewQuotation] = useState({ amount: '', status: 'sent', notes: '' });
  const [newFollowUp, setNewFollowUp] = useState({ date: '', time: '', follow_up_type: 'call', priority: 'medium', notes: '', reminder: true, reminder_before: 30, reminder_custom: '' });
  const [serialInput, setSerialInput] = useState('');
  const [serialEdit, setSerialEdit] = useState({ open: false, value: '', reason: '' });
  const [payInput, setPayInput] = useState({ amount: '', stage: '', schedule: 'milestone', notes: '', mode: 'cash', reference: '' });
  const [ledger, setLedger] = useState(null);
  const [recordingPay, setRecordingPay] = useState(false);
  const [cvEdit, setCvEdit] = useState({ open: false, value: '', reason: '' });
  const [scope, setScope] = useState(null);
  const [leadProjects, setLeadProjects] = useState([]);
  const [scopeInput, setScopeInput] = useState({ name: '', category: '', description: '', chargeable: true, amount: '' });
  const [savingScope, setSavingScope] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [activeReceipt, setActiveReceipt] = useState(null);
  const [showCall, setShowCall] = useState(false);
  const [callForm, setCallForm] = useState({ call_result: 'Connected', disposition: '', talk_duration_seconds: '', notes: '', follow_up_required: false, next_follow_up_at: '' });
  const [archIntel, setArchIntel] = useState(null);
  const [selectedArch, setSelectedArch] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [assigningArch, setAssigningArch] = useState(false);
  const [salesIntel, setSalesIntel] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [pickExec, setPickExec] = useState('');
  const [salesAssignReason, setSalesAssignReason] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  // Keep the tab in the URL so refresh and browser back/forward land on the same tab
  const urlTab = searchParams.get('tab') || 'overview';
  useEffect(() => { setActiveTab(urlTab); }, [urlTab]);
  const openTab = useCallback((tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'overview' ? {} : { tab });
  }, [setSearchParams]);
  const [newNote, setNewNote] = useState('');
  const [newWhatsappMessage, setNewWhatsappMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const isArchitectOrEngineer = ['architect', 'site_engineer'].includes(user?.role);

  const fetchLeadData = useCallback(async () => {
    try {
      const [leadRes, notesRes, activitiesRes, callsRes, whatsappRes, emailsRes, quotesRes, statusRes, historyRes, svcRes, attachmentsRes] = await Promise.all([
        leadAPI.getById(id),
        noteAPI.getByLeadId(id),
        activityAPI.getByLeadId(id),
        communicationAPI.getCallsByLeadId(id),
        communicationAPI.getWhatsAppByLeadId(id),
        communicationAPI.getEmailsByLeadId(id),
        quotationAPI.getByLead(id).catch(() => ({ data: [] })),
        leadStatusAPI.list().catch(() => ({ data: [] })),
        workflowAPI.history(id).catch(() => ({ data: [] })),
        settingsAPI.getServices().catch(() => ({ data: [] })),
        leadAPI.getAttachments(id).catch(() => ({ data: [] }))
      ]);
      setLead(leadRes.data);
      setNotes(notesRes.data);
      setActivities(activitiesRes.data);
      setCalls(callsRes.data);
      setWhatsappMessages(whatsappRes.data);
      setEmails(emailsRes.data);
      setQuotations(quotesRes.data);
      setStatusOptions((statusRes.data || []).filter(s => s.is_active));
      setWorkflowHistory(historyRes.data || []);
      setServices(svcRes.data || []);
      setAttachments(attachmentsRes.data || []);

      // Load stages for lead's primary service (first services_required)
      const lead = leadRes.data;
      const svcName = lead.services_required?.[0];
      if (svcName) {
        const svc = (svcRes.data || []).find(s => s.name === svcName);
        if (svc) {
          const stagesRes = await workflowAPI.listStages(svc.id).catch(() => ({ data: [] }));
          setWorkflowStages(stagesRes.data || []);
        }
      }
    } catch (error) {
      toast.error('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLeadData();
  }, [fetchLeadData]);

  const loadFollowUps = useCallback(async () => {
    try {
      const { data } = await followUpAPI.getAll({ lead_id: id });
      const arr = Array.isArray(data) ? data : (data.follow_ups || data.items || []);
      setFollowUps(arr.filter(f => String(f.lead_id) === String(id)));
    } catch (e) { /* ignore */ }
  }, [id]);

  useEffect(() => { loadFollowUps(); }, [loadFollowUps]);

  const loadArchIntel = useCallback(async () => {
    try { const res = await architectAssignAPI.intelligence(); setArchIntel(res.data); } catch (e) { /* not authorized */ }
  }, []);

  useEffect(() => { if (activeTab === 'project') loadArchIntel(); }, [activeTab, loadArchIntel]);

  const loadReceipts = useCallback(async () => {
    if (!hasPermission('payment.view')) return;
    try { const r = await receiptAPI.list(id); setReceipts(r.data); } catch (e) { /* silent */ }
  }, [id, hasPermission]);

  useEffect(() => { if (activeTab === 'project') loadReceipts(); }, [activeTab, loadReceipts]);

  const loadLedger = useCallback(async () => {
    if (!hasPermission('payment.view')) return;
    try { const r = await paymentAPI.ledger(id); setLedger(r.data); } catch (e) { /* silent */ }
  }, [id, hasPermission]);

  useEffect(() => { if (activeTab === 'project') loadLedger(); }, [activeTab, loadLedger]);

  const loadScope = useCallback(async () => {
    if (!hasPermission('scope.view')) return;
    try { const r = await scopeAPI.list(id); setScope(r.data); } catch (e) { /* silent */ }
  }, [id, hasPermission]);

  useEffect(() => { if (activeTab === 'project') loadScope(); }, [activeTab, loadScope]);

  // B5B — projects created from this lead (a Won lead may have zero, one or many)
  const loadLeadProjects = useCallback(async () => {
    try { const r = await projectAPI.forLead(id); setLeadProjects(r.data || []); } catch (e) { /* silent */ }
  }, [id]);

  useEffect(() => { if (activeTab === 'project') loadLeadProjects(); }, [activeTab, loadLeadProjects]);

  const afterScopeChange = async () => {
    await Promise.all([loadScope(), loadLedger(), fetchLeadData()]);
    window.dispatchEvent(new CustomEvent('lead360:refresh'));
  };

  const handleScopeCreate = async () => {
    if (!scopeInput.name.trim()) { toast.error('Enter the service / scope name'); return; }
    const amt = Number(scopeInput.amount || 0);
    if (scopeInput.chargeable && (!Number.isFinite(amt) || amt <= 0)) { toast.error('A chargeable scope item needs an amount greater than zero'); return; }
    setSavingScope(true);
    try {
      await scopeAPI.create(id, {
        name: scopeInput.name.trim(), category: scopeInput.category || null,
        description: scopeInput.description || null, chargeable: scopeInput.chargeable,
        amount: scopeInput.chargeable ? amt : 0, submit: false,
      });
      toast.success('Scope item created as Draft');
      setScopeInput({ name: '', category: '', description: '', chargeable: true, amount: '' });
      await afterScopeChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create scope item'); }
    finally { setSavingScope(false); }
  };

  const scopeAct = async (action, item) => {
    try {
      if (action === 'submit') { await scopeAPI.submit(id, item.id); toast.success('Submitted for approval'); }
      else if (action === 'approve') { await scopeAPI.approve(id, item.id); toast.success('Scope approved — contract value updated'); }
      else if (action === 'reject') {
        const r = window.prompt('Reason for rejection (mandatory):');
        if (!r || r.trim().length < 3) { if (r !== null) toast.error('A reason is required'); return; }
        await scopeAPI.reject(id, item.id, r.trim()); toast.success('Scope rejected');
      } else if (['in_progress', 'completed', 'closed'].includes(action)) {
        let r = '';
        if (action === 'closed') {
          r = window.prompt('Closure reason (mandatory):') || '';
          if (r.trim().length < 3) { toast.error('A closure reason is required'); return; }
        }
        await scopeAPI.setStatus(id, item.id, action, r.trim() || undefined);
        toast.success(`Scope ${action.replace('_', ' ')} — contract value unchanged`);
      } else if (action === 'cancel') {
        const r = window.prompt('Reason for cancellation (mandatory):');
        if (!r || r.trim().length < 3) { if (r !== null) toast.error('A reason is required'); return; }
        await scopeAPI.cancel(id, item.id, r.trim());
        toast.success('Scope cancelled — approved amount retained until a financial adjustment');
      } else if (action === 'modify') {
        const a = window.prompt('New amount for this scope item:', String(item.approved_amount ?? item.amount ?? ''));
        if (a === null) return;
        const amt = Number(a);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
        let r = '';
        if (item.approved_at) {
          r = window.prompt('Reason for changing an approved amount (mandatory):') || '';
          if (r.trim().length < 3) { toast.error('A reason is required'); return; }
        }
        await scopeAPI.modify(id, item.id, { amount: amt, reason: r.trim() || undefined });
        toast.success('Scope amount updated — net contract effect applied');
      } else if (action === 'adjust') {
        const d = window.prompt('Financial adjustment (use a negative value to reverse):');
        if (d === null) return;
        const delta = Number(d);
        if (!Number.isFinite(delta) || delta === 0) { toast.error('Enter a non-zero adjustment'); return; }
        const r = window.prompt('Reason for the financial adjustment (mandatory):');
        if (!r || r.trim().length < 3) { if (r !== null) toast.error('A reason is required'); return; }
        await scopeAPI.financialAdjust(id, item.id, delta, r.trim());
        toast.success('Financial adjustment applied and audited');
      }
      await afterScopeChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const handleSetSerial = async () => {
    if (!serialInput.trim()) { toast.error('Enter a serial number'); return; }
    try {
      await projectAPI.setSerial(id, serialInput.trim());
      toast.success('Project Serial assigned');
      setSerialInput('');
      fetchLeadData();
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign serial'); }
  };
  const handleEditSerial = async () => {
    if (!serialEdit.value.trim()) { toast.error('Enter the corrected serial number'); return; }
    if (serialEdit.reason.trim().length < 3) { toast.error('A reason is required to edit the serial'); return; }
    try {
      await projectAPI.editSerial(id, serialEdit.value.trim(), serialEdit.reason.trim());
      toast.success('Project Serial updated — change audited');
      setSerialEdit({ open: false, value: '', reason: '' });
      fetchLeadData();
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to edit serial'); }
  };
  const handleRecordPayment = async () => {
    const amt = Number(payInput.amount);
    if (!payInput.amount || !Number.isFinite(amt) || amt <= 0) { toast.error('Enter a valid payment amount greater than zero'); return; }
    setRecordingPay(true);
    try {
      await paymentAPI.record(id, {
        amount: amt, stage: payInput.stage, schedule: payInput.schedule, notes: payInput.notes,
        mode: payInput.mode, reference: payInput.reference,
        idempotency_key: `${id}-${amt}-${payInput.reference || ''}-${Date.now()}`,
      });
      toast.success('Payment recorded — confirm it to count towards Received');
      setPayInput({ amount: '', stage: '', schedule: 'milestone', notes: '', mode: 'cash', reference: '' });
      await Promise.all([fetchLeadData(), loadLedger()]);
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setRecordingPay(false); }
  };
  const handleConfirmPayment = async (paymentId) => {
    try {
      await paymentAPI.confirm(id, paymentId);
      toast.success('Payment confirmed');
      await Promise.all([fetchLeadData(), loadLedger()]);
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to confirm payment'); }
  };
  const handleReversePayment = async (paymentId) => {
    const reason = window.prompt('Reason for reversing this payment (mandatory):');
    if (!reason || reason.trim().length < 3) { if (reason !== null) toast.error('A reason is required'); return; }
    try {
      await paymentAPI.reverse(id, paymentId, reason.trim());
      toast.success('Payment reversed — original record preserved and audited');
      await Promise.all([fetchLeadData(), loadLedger()]);
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reverse payment'); }
  };
  const handleContractValue = async () => {
    const amt = Number(cvEdit.value);
    if (!Number.isFinite(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    if (cvEdit.reason.trim().length < 3) { toast.error('A reason is required'); return; }
    try {
      await paymentAPI.setContractValue(id, amt, cvEdit.reason.trim());
      toast.success('Approved Contract Value updated — change audited');
      setCvEdit({ open: false, value: '', reason: '' });
      await Promise.all([fetchLeadData(), loadLedger()]);
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update contract value'); }
  };
  const generateReceipt = async () => {
    try {
      const r = await receiptAPI.generate(id, {});
      setActiveReceipt(r.data); loadReceipts(); toast.success(`Receipt ${r.data.receipt_number} generated`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate receipt'); }
  };
  const printReceipt = () => {
    const el = document.getElementById('receipt-print-area');
    if (!el) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Receipt</title><style>body{font-family:sans-serif;padding:24px;color:#111} h2{margin:0} table{width:100%;border-collapse:collapse;margin-top:12px} td{padding:6px 4px;border-bottom:1px solid #eee;font-size:14px} .r{text-align:right}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  const submitCall = async () => {
    try {
      await callAPI.createCall({
        lead_id: id,
        phone_number: lead.primary_mobile || lead.mobile || '',
        direction: 'outgoing',
        call_result: callForm.call_result,
        disposition: callForm.disposition || undefined,
        talk_duration_seconds: callForm.talk_duration_seconds ? Number(callForm.talk_duration_seconds) : 0,
        notes: callForm.notes || undefined,
        follow_up_required: callForm.follow_up_required,
        next_follow_up_at: callForm.follow_up_required && callForm.next_follow_up_at ? new Date(callForm.next_follow_up_at).toISOString() : undefined,
      });
      toast.success('Call logged');
      setShowCall(false);
      setCallForm({ call_result: 'Connected', disposition: '', talk_duration_seconds: '', notes: '', follow_up_required: false, next_follow_up_at: '' });
      fetchLeadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to log call'); }
  };
  const handleOverrideGate = async () => {
    const reason = window.prompt('Reason for overriding the payment gate:');
    if (!reason) return;
    try { await paymentAPI.overrideGate(id, reason); toast.success('Payment gate overridden'); fetchLeadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Not permitted to override'); }
  };
  const handleAssignArchitect = async () => {
    if (!selectedArch) { toast.error('Select an architect'); return; }
    const isReassign = !!lead?.assigned_architect_id;
    if (isReassign && !assignReason.trim()) { toast.error('A reason is required to reassign an Architect'); return; }
    setAssigningArch(true);
    try {
      await architectAssignAPI.assign(id, selectedArch, assignReason.trim() || undefined);
      toast.success(isReassign ? 'Architect reassigned' : 'Architect assigned');
      setAssignReason('');
      setSelectedArch('');
      await fetchLeadData();
      await loadArchIntel();
      window.dispatchEvent(new CustomEvent('lead360:refresh'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign architect');
    } finally { setAssigningArch(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await noteAPI.create({ lead_id: id, content: newNote, attachments: [] });
      toast.success('Note added successfully');
      setNewNote('');
      fetchLeadData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleAttachmentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingAttachment(true);
    try { await leadAPI.uploadAttachment(id, file); toast.success('Attachment uploaded'); fetchLeadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Attachment upload failed'); }
    finally { setUploadingAttachment(false); event.target.value = ''; }
  };

  const handleSendWhatsApp = async () => {
    if (!newWhatsappMessage.trim()) return;
    try {
      await communicationAPI.sendWhatsApp({
        lead_id: id,
        message: newWhatsappMessage,
        message_type: 'text'
      });
      toast.success('WhatsApp message sent');
      setNewWhatsappMessage('');
      fetchLeadData();
    } catch (error) {
      toast.error('Failed to send WhatsApp message');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await leadAPI.update(id, { status: newStatus });
      toast.success('Status updated');
      fetchLeadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lead) {
    return <div className="text-center py-12">Lead not found</div>;
  }

  const statuses = statusOptions.length
    ? statusOptions.map(s => s.key || s.name.toLowerCase().replace(/\s+/g, '_'))
    : ['new_lead', 'contacted', 'interested', 'quotation_sent', 'negotiation', 'won', 'lost'];

  const getStatusColor = (status) => {
    const colors = {
      new_lead: 'bg-blue-500',
      hot_lead: 'bg-orange-500',
      converted: 'bg-green-500',
      lost: 'bg-red-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  const getActivityIcon = (type) => {
    const icons = {
      lead_created: <Plus className="h-4 w-4" />,
      lead_assigned: <User className="h-4 w-4" />,
      status_changed: <CheckCircle className="h-4 w-4" />,
      call_made: <PhoneCall className="h-4 w-4" />,
      whatsapp_sent: <MessageSquare className="h-4 w-4" />,
      email_sent: <Mail className="h-4 w-4" />,
      note_added: <FileText className="h-4 w-4" />,
      follow_up_scheduled: <CalendarIcon className="h-4 w-4" />,
      follow_up_added: <CalendarIcon className="h-4 w-4" />,
      meeting_scheduled: <CalendarIcon className="h-4 w-4" />,
      site_visit: <MapPin className="h-4 w-4" />,
      lead_reassigned: <User className="h-4 w-4" />,
      lead_transferred: <User className="h-4 w-4" />,
      tags_updated: <Building className="h-4 w-4" />,
      document_uploaded: <FileText className="h-4 w-4" />,
      quotation_sent: <FileText className="h-4 w-4" />,
      quotation_approved: <CheckCircle className="h-4 w-4" />,
      converted_to_customer: <CheckCircle className="h-4 w-4" />
    };
    return icons[type] || <Clock className="h-4 w-4" />;
  };

  const getActivityColor = (type) => {
    const colors = {
      call_made: 'bg-blue-500',
      whatsapp_sent: 'bg-green-500',
      email_sent: 'bg-purple-500',
      note_added: 'bg-primary',
      follow_up_scheduled: 'bg-yellow-500',
      follow_up_added: 'bg-yellow-500',
      meeting_scheduled: 'bg-purple-500',
      site_visit: 'bg-emerald-500',
      status_changed: 'bg-cyan-500',
      lead_reassigned: 'bg-blue-500',
      lead_transferred: 'bg-blue-500',
      tags_updated: 'bg-slate-500',
      document_uploaded: 'bg-indigo-500',
      quotation_sent: 'bg-primary',
      quotation_approved: 'bg-green-600',
      converted_to_customer: 'bg-green-600'
    };
    return colors[type] || 'bg-slate-500';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'timeline', label: 'Timeline', count: activities.length },
    { id: 'followup', label: 'Follow-ups', count: followUps.length },
    { id: 'project', label: 'Project & Payment', count: null },
    { id: 'workflow', label: 'Workflow', count: workflowHistory.length },
    { id: 'calls', label: 'Calls', count: calls.length },
    { id: 'whatsapp', label: 'WhatsApp', count: whatsappMessages.length },
    { id: 'emails', label: 'Emails', count: emails.length },
    { id: 'notes', label: 'Notes', count: notes.length },
    { id: 'attachments', label: 'Attachments', count: attachments.length },
    ...(isArchitectOrEngineer ? [] : [{ id: 'quotations', label: 'Quotations', count: quotations.length }])
  ];

  const handleScheduleFollowUp = async () => {
    if (!newFollowUp.date) { toast.error('Please pick a follow-up date'); return; }
    try {
      const dt = new Date(`${newFollowUp.date}T${newFollowUp.time || '09:00'}:00`);
      let reminderBefore = Number(newFollowUp.reminder_before) || 0;
      if (String(newFollowUp.reminder_before) === 'custom' && newFollowUp.reminder_custom) {
        const custom = new Date(newFollowUp.reminder_custom);
        reminderBefore = Math.max(0, Math.round((dt.getTime() - custom.getTime()) / 60000));
      }
      await leadFollowUpAPI.add(id, {
        scheduled_date: dt.toISOString(),
        follow_up_type: newFollowUp.follow_up_type,
        priority: newFollowUp.priority,
        notes: newFollowUp.notes,
        reminder: newFollowUp.reminder,
        reminder_before: reminderBefore,
      });
      toast.success('Follow-up scheduled (synced with Calendar)');
      setNewFollowUp({ date: '', time: '', follow_up_type: 'call', priority: 'medium', notes: '', reminder: true, reminder_before: 30, reminder_custom: '' });
      loadFollowUps();
      fetchLeadData();
    } catch (e) {
      toast.error('Failed to schedule follow-up');
    }
  };

  const nextFollowUp = [...followUps].filter(f => !f.completed).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))[0];
  const reminderLabel = (m) => {
    if (m == null) return '—';
    if (m === 0) return 'At follow-up time';
    if (m === 1440) return '1 day before';
    if (m >= 60) return `${m / 60} hour(s) before`;
    return `${m} min before`;
  };
  const fuComplete = async (fu) => {
    try { await followUpAPI.update(fu.id || fu._id, { completed: true, outcome: 'completed' }); toast.success('Follow-up completed'); loadFollowUps(); fetchLeadData(); }
    catch (e) { toast.error('Failed to complete'); }
  };
  const fuCancel = async (fu) => {
    if (!window.confirm('Cancel this follow-up? Its reminder will be cleared.')) return;
    try { await followUpAPI.update(fu.id || fu._id, { completed: true, outcome: 'cancelled' }); toast.success('Follow-up cancelled'); loadFollowUps(); fetchLeadData(); }
    catch (e) { toast.error('Failed to cancel'); }
  };
  const fuReschedule = async (fu) => {
    const d = window.prompt('Reschedule to (YYYY-MM-DD HH:mm):');
    if (!d) return;
    const dt = new Date(d.includes('T') ? d : d.replace(' ', 'T'));
    if (isNaN(dt.getTime())) { toast.error('Invalid date/time'); return; }
    try { await followUpAPI.update(fu.id || fu._id, { scheduled_date: dt.toISOString() }); toast.success('Follow-up rescheduled (reminder moved)'); loadFollowUps(); fetchLeadData(); }
    catch (e) { toast.error('Failed to reschedule'); }
  };
  const fuCallCompleted = async (fu) => {
    try { await followUpAPI.callCompleted(fu.id || fu._id); toast.success('Call marked completed'); loadFollowUps(); fetchLeadData(); }
    catch (e) { toast.error('Failed to log call'); }
  };
  const fuAddNote = async (fu) => {
    const n = window.prompt('Add a note to this lead:');
    if (!n) return;
    try { await noteAPI.create({ lead_id: id, content: n, attachments: [] }); toast.success('Note added'); fetchLeadData(); }
    catch (e) { toast.error('Failed to add note'); }
  };

  const openSmartAssign = async () => {
    setShowAssign(true);
    try { const res = await assignmentAPI.intelligence(); setSalesIntel(res.data); setPickExec(res.data?.recommended_user_id || ''); }
    catch (e) { toast.error('Failed to load assignment intelligence'); }
  };
  const doSmartAssign = async () => {
    if (!pickExec) { toast.error('Select an executive'); return; }
    try {
      await leadAPI.assign(id, pickExec, salesAssignReason);
      toast.success('Lead assigned');
      setShowAssign(false); setSalesAssignReason('');
      fetchLeadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
  };

  const handleAddQuotation = async () => {
    if (!newQuotation.amount) return toast.error('Amount required');
    try {
      await quotationAPI.create({
        lead_id: id,
        amount: parseFloat(newQuotation.amount),
        status: newQuotation.status,
        notes: newQuotation.notes || null
      });
      toast.success('Quotation added');
      setNewQuotation({ amount: '', status: 'sent', notes: '' });
      fetchLeadData();
    } catch (e) { toast.error('Failed to add quotation'); }
  };

  const handleUpdateQuotationStatus = async (qId, status) => {
    try {
      await quotationAPI.update(qId, { status });
      toast.success('Updated');
      fetchLeadData();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteQuotation = async (qId) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await quotationAPI.delete(qId);
      toast.success('Deleted');
      fetchLeadData();
    } catch { toast.error('Failed'); }
  };

  const handleSoftDelete = async () => {
    if (!window.confirm('Move this lead to Recycle Bin? Super Admin can restore within 30 days.')) return;
    try { await leadAPI.delete(id); toast.success('Moved to Recycle Bin'); navigate('/leads'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleWorkflowMove = async (stageId) => {
    try {
      const { data } = await workflowAPI.move(id, stageId);
      toast.success(`Moved to ${data.stage_name} (${data.progress}%)`);
      fetchLeadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
  };

  const money = (v) => (v || v === 0 ? `₹${Number(v).toLocaleString('en-IN')}` : '—');

  const tabBar = (
    <div className="rounded-xl border border-slate-200 bg-white px-4 dark:border-slate-700 dark:bg-slate-900" data-testid="lead-detail-tabbar">
      <div className="flex gap-5 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => openTab(tab.id)}
            data-testid={`lead-detail-tab-${tab.id}`}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400'
            }`}
          >
            {tab.label}{tab.count !== null && <span className="ml-1 text-xs text-slate-400">({tab.count})</span>}
          </button>
        ))}
      </div>
    </div>
  );


  return (
    <div className="space-y-4" data-testid="lead-detail-shell">
      <div className="flex items-center gap-2 text-sm">
        <button onClick={() => navigate('/leads')} data-testid="lead-overview-back" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Leads
        </button>
        <span className="text-slate-300">/</span>
        <span className="font-medium text-slate-700 dark:text-slate-200">{lead.customer_name}</span>
      </div>

      {lead.approval_status && lead.approval_status !== 'approved' && (
        <div data-testid="lead-approval-banner" className={`clay-card p-4 border-l-4 ${lead.approval_status === 'rejected' ? 'border-red-500' : lead.approval_status === 'changes_requested' ? 'border-amber-500' : 'border-yellow-500'}`}>
          <p className="text-sm font-medium">
            {lead.approval_status === 'pending' && 'Pending Approval'}
            {lead.approval_status === 'rejected' && 'Rejected'}
            {lead.approval_status === 'changes_requested' && 'Changes Requested'}
          </p>
          <p className="text-xs text-muted-foreground">
            {lead.approval_status === 'pending' && 'This lead is awaiting admin/manager approval. Quotation & pipeline progression are blocked until approved.'}
            {lead.rejection_reason && `Reason: ${lead.rejection_reason}`}
          </p>
        </div>
      )}

      <LeadOverview
        leadId={id}
        tabBar={tabBar}
        onRefresh={fetchLeadData}
        onOpenTab={openTab}
        callButton={<ClickToCallButton lead={lead} allowChoose dataTestid="lead-click-to-call-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60" />}
        extraActions={[]}
        dangerAction={(hasPermission('leads.delete') || user?.lead_controls?.can_delete === true)
          ? { label: 'Delete Lead', testId: 'lead-detail-delete-btn', onClick: handleSoftDelete } : null}
        content={activeTab === 'overview' ? null : (
          <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900" data-testid={`lead-tab-body-${activeTab}`}>
              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-4">
                  {activities.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No activities yet</p>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700"></div>
                      {activities.map((activity, idx) => (
                        <div key={activity.id} className="relative flex gap-4 pb-6">
                          <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${getActivityColor(activity.activity_type)} text-white flex-shrink-0`}>
                            {getActivityIcon(activity.activity_type)}
                          </div>
                          <div className="flex-1 min-w-0 pb-2">
                            <p className="font-medium">{activity.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDateTime(activity.created_at)}
                              {activity.created_by_name && ` • ${activity.created_by_name}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Project & Payment Tab */}
              {activeTab === 'project' && (
                <div className="space-y-5" data-testid="project-tab">
                  {/* B5B — Projects created from this Lead. Creating one never blocks creating more. */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="lead-projects-section">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <h4 className="font-medium">Projects from this Lead</h4>
                        <p className="text-xs text-muted-foreground">
                          {leadProjects.length === 0
                            ? 'No project created yet — Won/Converted is a sales outcome, creating a Project is a separate step.'
                            : `${leadProjects.length} project(s) · each has its own serial, contract ledger, architect and workflow.`}
                        </p>
                      </div>
                      {hasPermission('projects.create') ? (
                        <button onClick={() => navigate(`/projects/new?lead=${id}`)} data-testid="create-project-btn"
                          className="px-4 py-2 bg-primary text-white rounded-lg text-sm">
                          {leadProjects.length === 0 ? 'Create Project' : 'Create Another Project'}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid="create-project-denied">Creating a Project requires the Create Project permission</span>
                      )}
                    </div>
                    {leadProjects.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="lead-projects-table">
                          <thead className="text-left text-muted-foreground text-xs">
                            <tr><th className="py-1">Serial</th><th>Project</th><th>Service</th><th>Status</th><th /></tr>
                          </thead>
                          <tbody>
                            {leadProjects.map((p) => (
                              <tr key={p.project_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`lead-project-row-${p.project_id}`}>
                                <td className="py-2 font-mono">{p.project_serial}</td>
                                <td>{p.project_name || '\u2014'}</td>
                                <td>{p.service || (p.services_required || []).join(', ') || '\u2014'}</td>
                                <td>{p.project_status || '\u2014'}</td>
                                <td className="text-right">
                                  <button onClick={() => navigate(`/projects/${p.project_id}`)} data-testid={`open-project-${p.project_id}`}
                                    className="text-primary underline">Open Project</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Serial — one-time assignment, separate audited edit */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="serial-section">
                    <h4 className="font-medium mb-2">Project Serial Number</h4>
                    {lead.project_serial ? (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Assigned Serial</p>
                          <p className="font-mono text-lg font-semibold" data-testid="serial-value">{lead.project_serial}</p>
                        </div>
                        {hasPermission('project.serial.edit') ? (
                          <button onClick={() => setSerialEdit({ open: true, value: lead.project_serial, reason: '' })}
                            data-testid="serial-edit-btn"
                            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">Edit Serial</button>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid="serial-edit-denied">Serial editing requires the Edit Serial permission</span>
                        )}
                      </div>
                    ) : hasPermission('project.serial.assign') ? (
                      <>
                        <p className="text-sm text-amber-600 mb-2">No serial yet — required before Architect assignment.</p>
                        <div className="flex gap-2 mt-2">
                          <input value={serialInput} onChange={(e) => setSerialInput(e.target.value)} placeholder="e.g. GNP-2026-001" data-testid="serial-input"
                            className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                          <button onClick={handleSetSerial} data-testid="serial-save" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Assign Serial</button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid="serial-assign-denied">
                        No serial assigned. Assigning a Project Serial Number requires the Assign Serial permission.
                      </p>
                    )}
                    {serialEdit.open && (
                      <div className="mt-3 border-t border-slate-100 dark:border-slate-800 pt-3 space-y-2" data-testid="serial-edit-form">
                        <input value={serialEdit.value} onChange={(e) => setSerialEdit({ ...serialEdit, value: e.target.value })}
                          placeholder="Corrected serial number" data-testid="serial-edit-value"
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <input value={serialEdit.reason} onChange={(e) => setSerialEdit({ ...serialEdit, reason: e.target.value })}
                          placeholder="Reason for the change (mandatory)" data-testid="serial-edit-reason"
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <div className="flex gap-2">
                          <button onClick={handleEditSerial} data-testid="serial-edit-submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Save Change</button>
                          <button onClick={() => setSerialEdit({ open: false, value: '', reason: '' })} data-testid="serial-edit-cancel"
                            className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">Cancel</button>
                        </div>
                        {(lead.project_serial_history || []).length > 0 && (
                          <div className="text-xs text-muted-foreground space-y-1" data-testid="serial-history">
                            {(lead.project_serial_history || []).slice(-3).reverse().map((h, i) => (
                              <p key={i}>{h.previous_serial} → {h.new_serial} · {h.reason} · {h.changed_by_name || 'Unknown'} · {(h.changed_at || '').slice(0, 16).replace('T', ' ')}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Payment Details — authoritative Contract Ledger */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="payment-section">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Payment Details</h4>
                      {lead.payment_hold && <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Payment Hold</span>}
                    </div>
                    {/* Totals are computed by the backend from the ledger — never stored/editable */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-3">
                      <div>
                        <div className="text-muted-foreground text-xs">Base Contract Value</div>
                        <span data-testid="ledger-base">{money(ledger?.base_contract_value)}</span>
                        {hasPermission('payment.contract_value.edit') && (
                          <button onClick={() => setCvEdit({ open: true, value: String(ledger?.base_contract_value ?? ''), reason: '' })}
                            data-testid="contract-value-edit-btn" className="ml-2 text-xs text-primary underline">Edit</button>
                        )}
                      </div>
                      <div><div className="text-muted-foreground text-xs">Approved Additional Scope</div><span data-testid="ledger-scope">{money(ledger?.approved_additional_scope)}</span></div>
                      <div><div className="text-muted-foreground text-xs">Current Contract Value</div><span className="font-semibold" data-testid="ledger-approved">{money(ledger?.current_contract_value)}</span></div>
                      <div><div className="text-muted-foreground text-xs">Confirmed Received</div><span className="text-green-600" data-testid="ledger-received">{money(ledger?.total_received)}</span></div>
                      <div><div className="text-muted-foreground text-xs">Pending Balance</div><span className="text-orange-600" data-testid="ledger-pending">{money(ledger?.pending_balance)}</span></div>
                    </div>
                    {(ledger?.awaiting_confirmation || 0) > 0 && (
                      <p className="text-xs text-amber-600 mb-2" data-testid="ledger-awaiting">Awaiting confirmation: {money(ledger.awaiting_confirmation)} (not counted in Received)</p>
                    )}
                    {cvEdit.open && (
                      <div className="mb-3 space-y-2 border border-slate-200 dark:border-slate-700 rounded-lg p-3" data-testid="contract-value-form">
                        <input type="number" value={cvEdit.value} onChange={(e) => setCvEdit({ ...cvEdit, value: e.target.value })}
                          placeholder="Approved Contract Value" data-testid="contract-value-amount"
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <input value={cvEdit.reason} onChange={(e) => setCvEdit({ ...cvEdit, reason: e.target.value })}
                          placeholder="Reason for the change (mandatory)" data-testid="contract-value-reason"
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <div className="flex gap-2">
                          <button onClick={handleContractValue} data-testid="contract-value-submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Save Contract Value</button>
                          <button onClick={() => setCvEdit({ open: false, value: '', reason: '' })} data-testid="contract-value-cancel" className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">Cancel</button>
                        </div>
                        {(ledger?.contract_value_history || []).slice(-3).reverse().map((h, i) => (
                          <p key={i} className="text-xs text-muted-foreground">{h.previous_amount ?? '—'} → {h.new_amount} · {h.reason} · {h.changed_by_name} · {(h.changed_at || '').slice(0, 16).replace('T', ' ')}</p>
                        ))}
                      </div>
                    )}
                    {hasPermission('payment.record') && (
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                        <input type="number" value={payInput.amount} onChange={(e) => setPayInput({ ...payInput, amount: e.target.value })} placeholder="Payment amount" data-testid="pay-amount"
                          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <select value={payInput.mode} onChange={(e) => setPayInput({ ...payInput, mode: e.target.value })} data-testid="pay-mode"
                          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                          {['cash', 'cheque', 'neft', 'upi', 'card', 'other'].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                        </select>
                        <input value={payInput.reference} onChange={(e) => setPayInput({ ...payInput, reference: e.target.value })} placeholder="Txn / cheque no." data-testid="pay-reference"
                          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <input value={payInput.stage} onChange={(e) => setPayInput({ ...payInput, stage: e.target.value })} placeholder="Milestone / stage" data-testid="pay-stage"
                          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <input value={payInput.notes} onChange={(e) => setPayInput({ ...payInput, notes: e.target.value })} placeholder="Note" data-testid="pay-notes"
                          className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        <button onClick={handleRecordPayment} disabled={recordingPay} data-testid="pay-record" className="px-3 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">{recordingPay ? 'Saving…' : 'Record Payment'}</button>
                      </div>
                    )}

                    {/* Ledger / payment history */}
                    <div className="mt-4 overflow-x-auto" data-testid="payment-ledger">
                      <table className="w-full min-w-[720px] text-xs">
                        <thead className="text-left text-muted-foreground">
                          <tr><th className="py-1">Date</th><th>Amount</th><th>Mode</th><th>Reference</th><th>Note</th><th>Recorded by</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                          {(ledger?.records || []).length === 0 ? (
                            <tr><td colSpan={8} className="py-3 text-muted-foreground">No payments recorded yet.</td></tr>
                          ) : (ledger.records.map(r => (
                            <tr key={r.id} className={`border-t border-slate-100 dark:border-slate-800 ${r.status === 'reversed' ? 'opacity-60 line-through' : ''}`} data-testid={`ledger-row-${r.id}`}>
                              <td className="py-1.5">{r.recorded_at ? format(new Date(r.recorded_at), 'd MMM yyyy, h:mm a') : '—'}</td>
                              <td className="font-medium">{money(r.amount)}</td>
                              <td>{(r.mode || '—').toUpperCase()}</td>
                              <td>{r.reference || '—'}</td>
                              <td className="max-w-[180px] truncate">{r.notes || r.stage || '—'}</td>
                              <td>{r.recorded_by_name || '—'}</td>
                              <td data-testid={`ledger-status-${r.id}`}>{r.status}{r.status === 'reversed' && r.reversal_reason ? ` (${r.reversal_reason})` : ''}</td>
                              <td className="whitespace-nowrap">
                                {r.status !== 'reversed' && r.status !== 'confirmed' && hasPermission('payment.confirm') && (
                                  <button onClick={() => handleConfirmPayment(r.id)} data-testid={`ledger-confirm-${r.id}`} className="text-green-600 underline mr-2">Confirm</button>
                                )}
                                {r.status !== 'reversed' && hasPermission('payment.reverse') && (
                                  <button onClick={() => handleReversePayment(r.id)} data-testid={`ledger-reverse-${r.id}`} className="text-red-600 underline">Reverse</button>
                                )}
                              </td>
                            </tr>
                          )))}
                        </tbody>
                      </table>
                    </div>
                  {/* B4: Additional Services / Scope */}
                  {hasPermission('scope.view') && (
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800" data-testid="scope-section">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <h4 className="font-medium">Additional Services / Scope</h4>
                        <p className="text-xs text-muted-foreground" data-testid="scope-financial-impact">
                          Approved additional scope: <span className="font-semibold">{money(scope?.approved_additional_scope)}</span>
                          {' '}· Current Contract Value: <span className="font-semibold">{money(scope?.current_contract_value)}</span>
                        </p>
                      </div>

                      {hasPermission('scope.create') && (
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3">
                          <input value={scopeInput.name} onChange={(e) => setScopeInput({ ...scopeInput, name: e.target.value })} placeholder="Service / scope name" data-testid="scope-name"
                            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                          <input value={scopeInput.category} onChange={(e) => setScopeInput({ ...scopeInput, category: e.target.value })} placeholder="Category" data-testid="scope-category"
                            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                          <input value={scopeInput.description} onChange={(e) => setScopeInput({ ...scopeInput, description: e.target.value })} placeholder="Description" data-testid="scope-description"
                            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                          <select value={scopeInput.chargeable ? 'yes' : 'no'} onChange={(e) => setScopeInput({ ...scopeInput, chargeable: e.target.value === 'yes' })} data-testid="scope-chargeable"
                            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                            <option value="yes">Chargeable</option>
                            <option value="no">Non-Chargeable</option>
                          </select>
                          <input type="number" value={scopeInput.amount} disabled={!scopeInput.chargeable} onChange={(e) => setScopeInput({ ...scopeInput, amount: e.target.value })} placeholder="Amount" data-testid="scope-amount"
                            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm disabled:opacity-40" />
                          <button onClick={handleScopeCreate} disabled={savingScope} data-testid="scope-create" className="px-3 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">{savingScope ? 'Saving…' : 'Add Scope'}</button>
                        </div>
                      )}

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[820px] text-xs" data-testid="scope-table">
                          <thead className="text-left text-muted-foreground">
                            <tr><th className="py-1">Service / Scope</th><th>Category</th><th>Type</th><th>Amount</th><th>Financial impact</th><th>Status</th><th>Responsible</th><th>Actions</th></tr>
                          </thead>
                          <tbody>
                            {(scope?.items || []).length === 0 ? (
                              <tr><td colSpan={8} className="py-3 text-muted-foreground">No additional scope on this project.</td></tr>
                            ) : scope.items.map(s => (
                              <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800 align-top" data-testid={`scope-row-${s.id}`}>
                                <td className="py-1.5">
                                  <div className="font-medium">{s.name}</div>
                                  {s.description && <div className="text-muted-foreground max-w-[220px] truncate">{s.description}</div>}
                                  {(s.closure_reason || s.cancellation_reason || s.rejection_reason) && (
                                    <div className="text-muted-foreground">Reason: {s.closure_reason || s.cancellation_reason || s.rejection_reason}</div>
                                  )}
                                </td>
                                <td>{s.category || '—'}</td>
                                <td>{s.chargeable ? 'Chargeable' : 'Non-Chargeable'}</td>
                                <td>{s.chargeable ? money(s.approved_amount ?? s.amount) : '₹0'}</td>
                                <td data-testid={`scope-impact-${s.id}`}>{s.counts_financially ? money(s.financial_impact) : '₹0'}</td>
                                <td data-testid={`scope-status-${s.id}`}>{(s.status || '').replace(/_/g, ' ')}</td>
                                <td>
                                  <div>{s.created_by_name || '—'}</div>
                                  {s.approved_by_name && <div className="text-muted-foreground">appr: {s.approved_by_name}</div>}
                                </td>
                                <td className="whitespace-nowrap space-x-2">
                                  {s.status === 'draft' && hasPermission('scope.submit') && <button onClick={() => scopeAct('submit', s)} data-testid={`scope-submit-${s.id}`} className="text-primary underline">Submit</button>}
                                  {(s.status === 'draft' || s.status === 'pending_approval') && hasPermission('scope.approve') && <button onClick={() => scopeAct('approve', s)} data-testid={`scope-approve-${s.id}`} className="text-green-600 underline">Approve</button>}
                                  {s.status === 'pending_approval' && hasPermission('scope.approve') && <button onClick={() => scopeAct('reject', s)} data-testid={`scope-reject-${s.id}`} className="text-red-600 underline">Reject</button>}
                                  {s.approved_at && !['cancelled', 'rejected'].includes(s.status) && hasPermission('scope.close') && (
                                    <>
                                      {s.status === 'approved' && <button onClick={() => scopeAct('in_progress', s)} data-testid={`scope-start-${s.id}`} className="text-blue-600 underline">Start</button>}
                                      {['approved', 'in_progress'].includes(s.status) && <button onClick={() => scopeAct('completed', s)} data-testid={`scope-complete-${s.id}`} className="text-blue-600 underline">Complete</button>}
                                      {s.status !== 'closed' && <button onClick={() => scopeAct('closed', s)} data-testid={`scope-close-${s.id}`} className="text-slate-600 underline">Close</button>}
                                    </>
                                  )}
                                  {!['cancelled', 'rejected'].includes(s.status) && hasPermission('scope.modify') && <button onClick={() => scopeAct('modify', s)} data-testid={`scope-modify-${s.id}`} className="text-amber-600 underline">Edit ₹</button>}
                                  {!['cancelled', 'rejected'].includes(s.status) && hasPermission('scope.cancel') && <button onClick={() => scopeAct('cancel', s)} data-testid={`scope-cancel-${s.id}`} className="text-red-600 underline">Cancel</button>}
                                  {s.counts_financially && hasPermission('scope.financial_adjust') && <button onClick={() => scopeAct('adjust', s)} data-testid={`scope-adjust-${s.id}`} className="text-red-600 underline">Adjust ₹</button>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-2 text-[11px] text-muted-foreground">Completing, closing or cancelling a scope item never changes the contract value — use “Adjust ₹” for an audited financial reversal.</p>
                    </div>
                  )}

                  <button onClick={handleOverrideGate} data-testid="pay-override" className="mt-2 text-xs text-red-600 underline">Override payment gate (requires permission + reason)</button>
                    {hasPermission('payment.receipt.generate') && (
                      <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium">Receipts</h5>
                          <button onClick={generateReceipt} data-testid="receipt-generate" className="px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-lg">Generate Receipt</button>
                        </div>
                        {receipts.length === 0 ? <p className="text-xs text-muted-foreground">No receipts generated yet.</p> : (
                          <div className="space-y-1">
                            {receipts.map(r => (
                              <button key={r.id} onClick={() => setActiveReceipt(r)} data-testid="receipt-item" className="w-full text-left text-xs p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 flex justify-between">
                                <span className="font-mono">{r.receipt_number}</span>
                                <span>{r.amount_received != null ? `₹${Number(r.amount_received).toLocaleString('en-IN')}` : ''} · {(r.payment_date || '').slice(0, 10)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Receipt modal */}
                  {activeReceipt && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setActiveReceipt(null)}>
                      <div className="clay-card p-6 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="receipt-modal">
                        <div id="receipt-print-area">
                          {activeReceipt.company?.logo_url ? <img src={activeReceipt.company.logo_url} alt="logo" style={{ height: 44, objectFit: 'contain', marginBottom: 6 }} /> : null}
                          <h2 style={{ color: '#F59E0B' }}>{activeReceipt.company?.name || 'GNONS'}</h2>
                          {activeReceipt.company?.address ? <p style={{ fontSize: 11, color: '#666', margin: '2px 0' }}>{activeReceipt.company.address}</p> : null}
                          <p style={{ fontSize: 11, color: '#666', margin: '2px 0' }}>
                            {activeReceipt.company?.gstin ? `GSTIN: ${activeReceipt.company.gstin}` : ''}
                            {activeReceipt.company?.phone ? ` · ${activeReceipt.company.phone}` : ''}
                            {activeReceipt.company?.email ? ` · ${activeReceipt.company.email}` : ''}
                          </p>
                          <p style={{ fontSize: 12, color: '#111', fontWeight: 600, marginTop: 8 }}>Payment Receipt</p>
                          <table>
                            <tbody>
                              <tr><td>Receipt No.</td><td className="r">{activeReceipt.receipt_number}</td></tr>
                              <tr><td>Date</td><td className="r">{(activeReceipt.payment_date || '').slice(0, 10)}</td></tr>
                              <tr><td>Client</td><td className="r">{activeReceipt.client}</td></tr>
                              <tr><td>Project</td><td className="r">{activeReceipt.project_name || activeReceipt.project_serial || '—'}</td></tr>
                              <tr><td>Project Serial</td><td className="r">{activeReceipt.project_serial || '—'}</td></tr>
                              <tr><td>Payment Stage</td><td className="r">{activeReceipt.payment_stage || '—'}</td></tr>
                              <tr><td>Amount Received</td><td className="r">₹{Number(activeReceipt.amount_received || 0).toLocaleString('en-IN')}</td></tr>
                              <tr><td>Payment Method</td><td className="r">{activeReceipt.payment_method || '—'}</td></tr>
                              <tr><td>Reference</td><td className="r">{activeReceipt.reference || '—'}</td></tr>
                              <tr><td>Total Deal Value</td><td className="r">₹{Number(activeReceipt.total_deal_value || 0).toLocaleString('en-IN')}</td></tr>
                              <tr><td>Previous Received</td><td className="r">₹{Number(activeReceipt.previous_received || 0).toLocaleString('en-IN')}</td></tr>
                              <tr><td>Total Received</td><td className="r">₹{Number(activeReceipt.total_received || 0).toLocaleString('en-IN')}</td></tr>
                              <tr><td>Outstanding</td><td className="r">₹{Number(activeReceipt.outstanding || 0).toLocaleString('en-IN')}</td></tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setActiveReceipt(null)} className="px-4 py-2 text-sm">Close</button>
                          <button onClick={printReceipt} data-testid="receipt-print" className="px-4 py-2 text-sm bg-primary text-white rounded-lg">Print / PDF</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Architect assignment with intelligence */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="architect-assign-section">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <h4 className="font-medium">{lead.assigned_architect_id ? 'Reassign Architect (Smart)' : 'Assign Architect (Smart)'}</h4>
                      <p className="text-xs" data-testid="current-architect">
                        <span className="text-muted-foreground">Currently assigned: </span>
                        <span className="font-medium">{archIntel?.architects?.find(a => a.user_id === lead.assigned_architect_id)?.name || (lead.assigned_architect_id ? 'Assigned' : 'Not Assigned')}</span>
                      </p>
                    </div>
                    {archIntel && archIntel.architects ? (
                      <>
                        {archIntel.recommended_reason && <p className="text-xs text-primary mb-2">Recommended: {(archIntel.architects.find(a => a.user_id === archIntel.recommended_user_id) || {}).name} — {archIntel.recommended_reason}</p>}
                        <div className="overflow-x-auto mb-3">
                          <table className="w-full text-xs">
                            <thead className="text-left text-muted-foreground"><tr><th className="py-1">Pick</th><th>Architect</th><th>Running</th><th>Not Started</th><th>Waiting</th><th>Revisions</th><th>Urgent</th><th>Workload</th></tr></thead>
                            <tbody>{archIntel.architects.map(a => (
                              <tr key={a.user_id} className={`border-t border-slate-100 dark:border-slate-800 ${a.user_id === archIntel.recommended_user_id ? 'bg-primary/5' : ''}`}>
                                <td className="py-1"><input type="radio" name="arch" value={a.user_id} checked={selectedArch === a.user_id} onChange={() => setSelectedArch(a.user_id)} data-testid={`arch-pick-${a.user_id}`} /></td>
                                <td>{a.name}{a.user_id === lead.assigned_architect_id ? ' (current)' : ''}</td><td>{a.running_projects}</td><td>{a.not_started}</td><td>{a.waiting_approval}</td><td>{a.revisions}</td><td>{a.urgent_projects}</td><td data-testid={`arch-workload-${a.user_id}`}>{a.current_workload}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                        <div className="flex gap-2">
                          <input value={assignReason} onChange={(e) => setAssignReason(e.target.value)} data-testid="arch-assign-reason"
                            placeholder={lead.assigned_architect_id ? 'Reason for reassignment (mandatory)' : 'Reason / comments (optional)'}
                            className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                          <button onClick={handleAssignArchitect} disabled={assigningArch} data-testid="arch-assign-btn" className="px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">
                            {assigningArch ? 'Saving…' : (lead.assigned_architect_id ? 'Reassign' : 'Assign')}
                          </button>
                        </div>
                        {!lead.project_serial && <p className="text-xs text-red-600 mt-2" data-testid="arch-gate-serial">A Project Serial Number must be assigned before an Architect can be assigned.</p>}
                        {lead.payment_hold && !lead.payment_gate_overridden && <p className="text-xs text-red-600 mt-2" data-testid="arch-gate-payment">Payment gate is active — outstanding payment must be confirmed first.</p>}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid="arch-intel-denied">Architect assignment requires the Assign Architect permission.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Follow-up Tab */}
              {activeTab === 'followup' && (
                <div className="space-y-4" data-testid="followup-tab">
                  {/* Next Follow-up */}
                  {nextFollowUp && (
                    <div className="border rounded-lg p-4 bg-primary/5 border-primary/30" data-testid="next-followup-card">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium flex items-center gap-2"><CalendarIcon className="h-4 w-4 text-primary" /> Next Follow-up</h4>
                        {new Date(nextFollowUp.scheduled_date) < new Date() && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="next-followup-overdue">Overdue</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                        <div><div className="text-muted-foreground text-xs">Date & Time</div>{formatDateTime(nextFollowUp.scheduled_date)}</div>
                        <div><div className="text-muted-foreground text-xs">Type</div><span className="capitalize">{(nextFollowUp.follow_up_type || 'call').replace(/_/g, ' ')}</span></div>
                        <div><div className="text-muted-foreground text-xs">Priority</div><span className="capitalize">{nextFollowUp.priority || 'medium'}</span></div>
                        <div><div className="text-muted-foreground text-xs">Reminder</div>{nextFollowUp.reminder === false ? 'Off' : reminderLabel(nextFollowUp.reminder_before)}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => fuComplete(nextFollowUp)} data-testid="next-fu-complete" className="px-3 py-1.5 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200">Complete</button>
                        <button onClick={() => fuCallCompleted(nextFollowUp)} data-testid="next-fu-call-completed" className="px-3 py-1.5 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Call Completed</button>
                        <button onClick={() => fuReschedule(nextFollowUp)} data-testid="next-fu-reschedule" className="px-3 py-1.5 text-xs rounded bg-amber-100 text-amber-700 hover:bg-amber-200">Reschedule</button>
                        <button onClick={() => fuAddNote(nextFollowUp)} data-testid="next-fu-note" className="px-3 py-1.5 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200">Add Note</button>
                        <button onClick={() => fuCancel(nextFollowUp)} data-testid="next-fu-cancel" className="px-3 py-1.5 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200">Cancel</button>
                      </div>
                    </div>
                  )}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <h4 className="font-medium mb-3">Schedule Follow-up</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Date *</label>
                        <input type="date" value={newFollowUp.date} onChange={(e) => setNewFollowUp({ ...newFollowUp, date: e.target.value })}
                          data-testid="fu-date" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Time</label>
                        <input type="time" value={newFollowUp.time} onChange={(e) => setNewFollowUp({ ...newFollowUp, time: e.target.value })}
                          data-testid="fu-time" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Type</label>
                        <select value={newFollowUp.follow_up_type} onChange={(e) => setNewFollowUp({ ...newFollowUp, follow_up_type: e.target.value })}
                          data-testid="fu-type" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                          <option value="call">Call</option>
                          <option value="meeting">Meeting</option>
                          <option value="site_visit">Site Visit</option>
                          <option value="video_call">Video Call</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Priority</label>
                        <select value={newFollowUp.priority} onChange={(e) => setNewFollowUp({ ...newFollowUp, priority: e.target.value })}
                          data-testid="fu-priority" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Reminder</label>
                        <select value={newFollowUp.reminder_before} onChange={(e) => setNewFollowUp({ ...newFollowUp, reminder_before: e.target.value })}
                          data-testid="fu-reminder-before" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                          <option value={0}>At follow-up time</option>
                          <option value={15}>15 minutes before</option>
                          <option value={30}>30 minutes before</option>
                          <option value={60}>1 hour before</option>
                          <option value={120}>2 hours before</option>
                          <option value={1440}>1 day before</option>
                          <option value="custom">Custom date/time</option>
                        </select>
                      </div>
                      {String(newFollowUp.reminder_before) === 'custom' && (
                        <div>
                          <label className="block text-xs font-medium mb-1">Custom reminder</label>
                          <input type="datetime-local" value={newFollowUp.reminder_custom} onChange={(e) => setNewFollowUp({ ...newFollowUp, reminder_custom: e.target.value })}
                            data-testid="fu-reminder-custom" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                        </div>
                      )}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium mb-1">Notes</label>
                        <input value={newFollowUp.notes} onChange={(e) => setNewFollowUp({ ...newFollowUp, notes: e.target.value })}
                          data-testid="fu-notes" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                      </div>
                      <div className="md:col-span-2 flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={newFollowUp.reminder} onChange={(e) => setNewFollowUp({ ...newFollowUp, reminder: e.target.checked })} data-testid="fu-reminder" />
                          Set reminder
                        </label>
                        <button onClick={handleScheduleFollowUp} data-testid="fu-submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Schedule</button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {followUps.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No follow-ups scheduled.</p>
                    ) : followUps.map((f) => (
                      <div key={f.id || f._id} className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-2 text-sm gap-2" data-testid="fu-item">
                        <span className="min-w-0">
                          <span className="block truncate capitalize">{(f.follow_up_type || 'call').replace(/_/g, ' ')} — {f.scheduled_date ? formatDateTime(f.scheduled_date) : ''}</span>
                          {f.notes && <span className="block truncate text-xs text-muted-foreground" data-testid="fu-item-agenda">{f.notes}</span>}
                          <span className="block text-xs text-muted-foreground">
                            {f.assigned_to_name ? `Assigned: ${f.assigned_to_name}` : 'Assigned: lead owner'}
                            {f.reminder_before ? ` · Reminder ${f.reminder_before}m before` : ''}
                            {f.created_by_name ? ` · Created by ${f.created_by_name}` : ''}
                            {f.completed && f.outcome ? ` · Outcome: ${f.outcome}` : ''}
                          </span>
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          {!f.completed && (
                            <>
                              <button onClick={() => fuComplete(f)} data-testid="fu-list-complete" className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200">Complete</button>
                              <button onClick={() => fuReschedule(f)} data-testid="fu-list-reschedule" className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700 hover:bg-amber-200">Reschedule</button>
                            </>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${f.completed ? (f.outcome === 'cancelled' ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700') : 'bg-amber-100 text-amber-700'}`}>{f.completed ? (f.outcome === 'cancelled' ? 'Cancelled' : 'Done') : 'Pending'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}


              {/* Workflow Tab */}
              {activeTab === 'workflow' && (
                <div className="space-y-4" data-testid="workflow-tab">
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <GitBranch className="h-4 w-4 text-primary" />
                      <p className="text-sm font-medium">Current Stage: {lead.workflow_stage_name || 'Not started'}</p>
                    </div>
                    {lead.workflow_progress != null && (
                      <div className="mb-3">
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full">
                          <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${lead.workflow_progress}%` }} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{lead.workflow_progress}% complete</p>
                      </div>
                    )}
                    {workflowStages.length > 0 ? (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Move to stage:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {workflowStages.filter(s => s.is_active).map(s => (
                            <button key={s._id || s.id}
                              data-testid={`wf-move-${s._id || s.id}`}
                              onClick={() => handleWorkflowMove(s._id || s.id)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${lead.workflow_stage_id === (s._id || s.id) ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary'}`}
                              style={lead.workflow_stage_id === (s._id || s.id) ? { backgroundColor: s.color, borderColor: s.color } : {}}>
                              {s.name}
                              {s.role_required && <span className="ml-1 opacity-60 text-[10px]">({s.role_required})</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                        Set a primary service (Services Required) on this lead to enable workflow moves.
                      </p>
                    )}
                  </div>
                  <p className="text-sm font-medium">History</p>
                  {workflowHistory.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6 text-sm">No workflow moves yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {[...workflowHistory].reverse().map((h, idx) => (
                        <div key={idx} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-sm">
                          <p><span className="text-muted-foreground">{h.from_stage_name || 'Start'}</span> → <span className="font-medium">{h.to_stage_name}</span></p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {h.moved_by_name} · {h.moved_at ? format(new Date(h.moved_at), 'MMM d, yyyy h:mm a') : ''}
                          </p>
                          {h.comment && <p className="text-xs mt-1">{h.comment}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Calls Tab */}
              {activeTab === 'calls' && (
                <div className="space-y-3">
                  {calls.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No calls yet</p>
                  ) : (
                    calls.map((call) => (
                      <div key={call.id} className="flex items-start gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg">
                        <div className={`p-2 rounded-full ${call.direction === 'outgoing' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                          {call.direction === 'outgoing' ? <PhoneOutgoing className="h-4 w-4" /> : <PhoneIncoming className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{call.outcome || 'Call logged'}</p>
                          <p className="text-sm text-muted-foreground">Duration: {call.duration}s • {call.created_by_name}</p>
                          {call.notes && <p className="text-sm mt-1">{call.notes}</p>}
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDateTime(call.created_at)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* WhatsApp Tab */}
              {activeTab === 'whatsapp' && (
                <div className="space-y-4">
                  <div className="space-y-3 max-h-96 overflow-y-auto p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    {whatsappMessages.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No messages yet</p>
                    ) : (
                      whatsappMessages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs px-4 py-2 rounded-lg ${
                            msg.direction === 'outgoing'
                              ? 'bg-green-500 text-white'
                              : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                          }`}>
                            <p className="text-sm">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.direction === 'outgoing' ? 'text-green-100' : 'text-muted-foreground'}`}>
                              {format(new Date(msg.created_at), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newWhatsappMessage}
                      onChange={(e) => setNewWhatsappMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendWhatsApp()}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900"
                    />
                    <button
                      onClick={handleSendWhatsApp}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Emails Tab */}
              {activeTab === 'emails' && (
                <div className="space-y-3">
                  {emails.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No emails yet</p>
                  ) : (
                    emails.map((email) => (
                      <div key={email.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium">{email.subject}</p>
                            <p className="text-sm text-muted-foreground">To: {email.to_email}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            email.opened ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {email.opened ? 'Opened' : 'Delivered'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{email.body}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {format(new Date(email.created_at), 'MMM d, yyyy h:mm a')} • {email.created_by_name}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                      placeholder="Add a note..."
                      className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900"
                    />
                    <button
                      onClick={handleAddNote}
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                    >
                      Add Note
                    </button>
                  </div>
                  <div className="space-y-3">
                    {notes.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No notes yet</p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                          <p className="text-sm">{note.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {note.created_by_name} • {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {activeTab === 'attachments' && (
                <div className="space-y-4" data-testid="lead-attachments-tab">
                  {hasPermission('leads.edit') && <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm cursor-pointer">
                    {uploadingAttachment ? 'Uploading…' : 'Upload attachment'}
                    <input type="file" accept="image/jpeg,image/png,image/webp,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleAttachmentUpload} className="hidden" disabled={uploadingAttachment} />
                  </label>}
                  <p className="text-xs text-muted-foreground">Images, PDF, Word, and Excel files up to 10 MB.</p>
                  {attachments.length === 0 ? <p className="text-center text-muted-foreground py-8">No attachments yet</p> : (
                    <div className="space-y-2">{attachments.map(file => <a key={file.id} href={leadAPI.attachmentDownloadUrl(id, file.id)} className="block border border-slate-200 dark:border-slate-700 rounded-lg p-3 hover:bg-slate-50 dark:hover:bg-slate-800" target="_blank" rel="noreferrer"><p className="font-medium text-sm">{file.filename}</p><p className="text-xs text-muted-foreground mt-1">{file.uploaded_by_name || 'Unknown'} · {format(new Date(file.uploaded_at), 'MMM d, yyyy h:mm a')} · {(file.file_size / 1024).toFixed(1)} KB</p></a>)}</div>
                  )}
                </div>
              )}
              {/* Quotations Tab */}
              {activeTab === 'quotations' && (
                <div className="space-y-4" data-testid="quotations-tab">
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium">New Quotation</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        type="number"
                        placeholder="Amount (₹)"
                        data-testid="quotation-amount-input"
                        value={newQuotation.amount}
                        onChange={(e) => setNewQuotation({ ...newQuotation, amount: e.target.value })}
                        className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                      />
                      <select
                        value={newQuotation.status}
                        onChange={(e) => setNewQuotation({ ...newQuotation, status: e.target.value })}
                        className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                      >
                        <option value="pending">Pending</option>
                        <option value="sent">Sent</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <button
                        onClick={handleAddQuotation}
                        data-testid="quotation-add-btn"
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm"
                      >
                        Add
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Notes (optional)"
                      value={newQuotation.notes}
                      onChange={(e) => setNewQuotation({ ...newQuotation, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                    />
                  </div>
                  {quotations.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No quotations yet</p>
                  ) : (
                    quotations.map(q => (
                      <div key={q.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid={`quotation-${q.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-lg">₹{Number(q.amount || 0).toLocaleString('en-IN')}</p>
                            <p className="text-xs text-muted-foreground">
                              By {q.created_by_name} · {format(new Date(q.created_at), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                          <select
                            value={q.status}
                            onChange={(e) => handleUpdateQuotationStatus(q.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${
                              q.status === 'approved' ? 'bg-green-100 text-green-700' :
                              q.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              q.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="sent">Sent</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                        {q.notes && <p className="text-sm text-muted-foreground mt-1">{q.notes}</p>}
                        <button
                          onClick={() => handleDeleteQuotation(q.id)}
                          className="mt-2 text-xs text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
          </div>
        )}
      />

      {/* Log Call — post-call disposition */}
      {showCall && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCall(false)}>
          <div className="clay-card p-6 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="log-call-modal">
            <h3 className="text-lg font-medium">Log Call — {lead.customer_name || lead.company_name}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Call Result</label>
                <select value={callForm.call_result} onChange={e => setCallForm({ ...callForm, call_result: e.target.value })}
                  data-testid="call-result" className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900">
                  {['Connected', 'No Answer', 'Busy', 'Failed', 'Rejected', 'Cancelled', 'Unknown'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Disposition</label>
                <select value={callForm.disposition} onChange={e => setCallForm({ ...callForm, disposition: e.target.value })}
                  data-testid="call-disposition" className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900">
                  <option value="">Select…</option>
                  {['Interested', 'Hot Lead', 'Follow-up Required', 'Quotation Required', 'Call Back Later', 'Not Interested', 'Wrong Number', 'Not Reachable', 'Already Converted', 'Other'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Talk Time (sec)</label>
                <input type="number" value={callForm.talk_duration_seconds} onChange={e => setCallForm({ ...callForm, talk_duration_seconds: e.target.value })}
                  data-testid="call-talk" className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={callForm.follow_up_required} onChange={e => setCallForm({ ...callForm, follow_up_required: e.target.checked })} data-testid="call-fu-required" />
                  Follow-up required
                </label>
              </div>
              {callForm.follow_up_required && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1">Next Follow-up</label>
                  <input type="datetime-local" value={callForm.next_follow_up_at} onChange={e => setCallForm({ ...callForm, next_follow_up_at: e.target.value })}
                    data-testid="call-fu-datetime" className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900" />
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1">Notes</label>
                <textarea value={callForm.notes} onChange={e => setCallForm({ ...callForm, notes: e.target.value })} rows={2}
                  data-testid="call-notes" className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {[['Hot', 'Hot Lead'], ['Not Interested', 'Not Interested'], ['Quotation', 'Quotation Required'], ['Call Back', 'Call Back Later'], ['Wrong Number', 'Wrong Number']].map(([label, val]) => (
                <button key={val} onClick={() => setCallForm({ ...callForm, disposition: val })} data-testid={`call-quick-${val.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`px-2 py-1 text-xs rounded ${callForm.disposition === val ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{label}</button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCall(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={submitCall} data-testid="call-submit" className="px-4 py-2 text-sm bg-primary text-white rounded-lg">Save Call</button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Assign Modal (inline Assignment Intelligence) */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAssign(false)}>
          <div className="clay-card p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto space-y-4" onClick={e => e.stopPropagation()} data-testid="smart-assign-modal">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-medium">Assign Lead — Smart Recommendation</h3>
                <p className="text-xs text-muted-foreground mt-1">Explainable, DB-derived workload & performance. Final choice is yours.</p>
              </div>
              <button onClick={() => setShowAssign(false)} className="text-muted-foreground">Close</button>
            </div>
            {!salesIntel ? (
              <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div>
            ) : (
              <>
                {salesIntel.recommended_reason && (
                  <div className="clay-card p-3 border-l-4 border-primary" data-testid="smart-assign-recommended">
                    <p className="text-sm"><span className="font-medium">Recommended:</span> {(salesIntel.executives.find(e => e.user_id === salesIntel.recommended_user_id) || {}).name} — {salesIntel.recommended_reason}</p>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-muted-foreground">
                      <tr>{['Pick', 'Executive', 'Active', 'Pending FU', 'Overdue FU', 'Hot', 'Won', 'Conversion', 'Avg Close', 'Workload', 'Score'].map(h => <th key={h} className="py-1 pr-2">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {salesIntel.executives.map(e => (
                        <tr key={e.user_id} className={`border-t border-slate-100 dark:border-slate-800 ${e.user_id === salesIntel.recommended_user_id ? 'bg-primary/5' : ''}`} data-testid="smart-assign-row">
                          <td className="py-1"><input type="radio" name="exec" value={e.user_id} checked={pickExec === e.user_id} onChange={() => setPickExec(e.user_id)} data-testid={`smart-assign-pick-${e.user_id}`} /></td>
                          <td className="pr-2">{e.name}</td>
                          <td>{e.active_leads}</td>
                          <td>{e.pending_follow_ups}</td>
                          <td className={e.overdue_follow_ups > 0 ? 'text-red-600 font-medium' : ''}>{e.overdue_follow_ups}</td>
                          <td>{e.hot_leads}</td>
                          <td>{e.won_leads}</td>
                          <td>{e.conversion_rate}%</td>
                          <td>{e.avg_closing_days ?? '—'}</td>
                          <td>{e.current_workload}</td>
                          <td className="font-medium">{e.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2">
                  <input value={salesAssignReason} onChange={(e) => setSalesAssignReason(e.target.value)} placeholder="Reason / comment (optional)"
                    data-testid="smart-assign-reason" className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                  <button onClick={doSmartAssign} data-testid="smart-assign-submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Assign</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
